import { lazy, Suspense, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Spin } from "antd";

// Lazy so importing todoRoutes/TodoLoadingShell eagerly (from index.tsx) does
// NOT drag the admin FormFieldRenderer (via PublicFillFormPage) into the
// funnel's main bundle. PublicFillFormPage renders only inside the <Wrap>
// <Suspense> below. See docs/infrastructure/CLIENT_BUNDLE_ARCHITECTURE.md.
const PublicFillFormPage = lazy(
  () => import("../components/todo/PublicFillFormPage"),
);
const PublicMedicalHistoryPage = lazy(
  () => import("../components/todo/PublicMedicalHistoryPage"),
);

// Tokenized, no-auth pages for coordinator-requested patient tasks
// (PatientTask). The token in the URL is the credential — no Google
// OAuth, no patient-portal session required.
//
// PR1 ships /todo/form/:linkToken (FILL_FORM). /todo/estimate and
// /todo/payment are added in PR2 and PR3.

// Full-viewport loader so the patient never sees a flash of unrelated
// content (e.g. the quote-funnel landing page) while the route's
// initial Relay query suspends. Exported so the /* catch-all
// dispatcher in index.tsx can reuse the same loading state.
export const TodoLoadingShell = () => (
  <div
    style={{
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: 16,
    }}
  >
    <Spin size="large" />
    <div style={{ color: "#666", fontSize: 14 }}>Loading your form…</div>
  </div>
);

// Full-viewport labelled loader for the medical-history questionnaire — mirrors
// TodoLoadingShell above so the patient never sees the funnel chrome bleed
// through while the Relay query suspends. Defined here (not in the page
// module) so index.tsx's eager import of the shell doesn't drag the page's
// lazy-loaded component in too. See docs/infrastructure/CLIENT_BUNDLE_ARCHITECTURE.md
// §2 and docs/infrastructure/PATIENT_FACING_EMAIL_LINKS.md.
export const MedicalHistoryLoadingShell = () => (
  <div
    style={{
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: 16,
    }}
  >
    <Spin size="large" />
    <div style={{ color: "#666", fontSize: 14 }}>
      Loading your medical questionnaire…
    </div>
  </div>
);

// Full-viewport labelled loader for the SIGN_CONTRACT fallback below — mirrors
// MedicalHistoryLoadingShell so the patient never sees the funnel chrome bleed
// through while the browser leaves for the server bridge. Exported so the /*
// catch-all dispatcher in index.tsx can reuse it. See
// docs/infrastructure/PATIENT_FACING_EMAIL_LINKS.md.
export const TodoSignLoadingShell = () => (
  <div
    style={{
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: 16,
    }}
  >
    <Spin size="large" />
    <div style={{ color: "#666", fontSize: 14 }}>Opening your document…</div>
  </div>
);

// Not lazy: it is a few lines and renders nothing but the shell above.
//
// This route exists only so a /action/todo/sign/<token> link copied from the
// admin before 2026-10-23 (when PatientTask.publicUrl emitted this dead shape,
// a page the client never served) still opens the signer. The canonical link is
// the server bridge /api/todo/sign-redirect/<token> (routes/todo-sign-redirect.ts,
// patientTaskService.buildPublicUrl): it mints a signer token and 302s to
// /portal/sign/<envelopeId>?t=... REACT_APP_API_URL is the deployed app base
// (prod https://topaesthetics.com/app/quote, per scripts/deploy-prod.sh), i.e.
// the same base the server's CLIENT_URL uses.
export const TodoSignRedirect = ({
  linkTokenOverride,
}: {
  // Set when rendered through the /* catch-all dispatcher in index.tsx rather
  // than the specific /action/todo/sign/:linkToken route.
  linkTokenOverride?: string;
} = {}) => {
  const { linkToken } = useParams();
  const token = linkTokenOverride || linkToken || "";

  useEffect(() => {
    // The static #initial-loading curtain in index.html stays up until
    // hideInitialLoading() runs (normally by QuoteApp); same convention as
    // PublicFillFormPage.
    (window as any).hideInitialLoading?.();
    window.location.replace(
      `${(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "")}/api/todo/sign-redirect/${encodeURIComponent(token)}`,
    );
  }, [token]);

  return <TodoSignLoadingShell />;
};

const Wrap = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<TodoLoadingShell />}>{children}</Suspense>
);

const MedHxWrap = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<MedicalHistoryLoadingShell />}>{children}</Suspense>
);

export const todoRoutes = [
  {
    // Under /action/ so the pre-React curtain (index.html isQuotePage())
    // treats it as a non-funnel page. Pre-/action/ links still resolve via
    // PublicCatchAllDispatcher's optional (?:action/)? match.
    path: "/action/todo/form/:linkToken",
    element: (
      <Wrap>
        <PublicFillFormPage />
      </Wrap>
    ),
  },
  {
    // PreOp medical-history questionnaire (Phase 2). Same /action/ + Suspense
    // pattern as the form route; PublicCatchAllDispatcher has a matching
    // defensive intercept. See docs/infrastructure/PATIENT_FACING_EMAIL_LINKS.md.
    path: "/action/todo/medical-history/:linkToken",
    element: (
      <MedHxWrap>
        <PublicMedicalHistoryPage />
      </MedHxWrap>
    ),
  },
  {
    // Fallback for SIGN_CONTRACT links copied before 2026-10-23: bounces to the
    // server bridge. See TodoSignRedirect above.
    path: "/action/todo/sign/:linkToken",
    element: <TodoSignRedirect />,
  },
];
